import React, { useState, useEffect } from 'react';
import Icon from './AppIcon';
import { useTranslation } from 'react-i18next';
import { useDarkMode } from '../hooks/useDarkMode';
import { supabase } from '../lib/supabase'; 

const Settings = () => {
  const { t } = useTranslation();
  const [isDark, setIsDark] = useDarkMode();
  const [loading, setLoading] = useState(true);
  const [userRole, setUserRole] = useState(null); 
  
  const [settings, setSettings] = useState({
    notifications: true,
    biometric: true,
    locationService: false,
    emailReports: true,
    autoLogout: false,
    autoDelete: false 
  });

  useEffect(() => {
    const fetchAziendaSettings = async () => {
      try {
        const { data: { user } } = await supabase.auth.getUser();
        if (!user) return;

        // 1. Popravljeno: Uzimamo 'ruolo'
        const { data: profil } = await supabase
          .from('profili')
          .select('id_azienda, ruolo')
          .eq('id', user.id)
          .single();

        if (profil) {
          // BITNO: ovdje mora biti profil.ruolo jer se tako zove kolona
          setUserRole(profil.ruolo); 

          if (profil.id_azienda) {
            const { data: azienda } = await supabase
              .from('aziende')
              .select('auto_delete')
              .eq('id', profil.id_azienda)
              .single();

            if (azienda) {
              setSettings(prev => ({ ...prev, autoDelete: azienda.auto_delete }));
            }
          }
        }
      } catch (err) {
        console.error("Greška pri dohvaćanju postavki:", err);
      } finally {
        setLoading(false);
      }
    };

    fetchAziendaSettings();
  }, []);

  const updateAziendaSetting = async (newValue) => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      const { data: profil } = await supabase
        .from('profili')
        .select('id_azienda')
        .eq('id', user.id)
        .single();

      if (profil?.id_azienda) {
        const { error } = await supabase
          .from('aziende')
          .update({ auto_delete: newValue })
          .eq('id', profil.id_azienda);

        if (error) throw error;
      }
    } catch (err) {
      console.error("Greška pri upisu u bazu:", err);
    }
  };

  const toggleSetting = async (id) => {
    if (id === 'darkMode') {
      setIsDark(!isDark);
    } else {
      const newValue = !settings[id];
      setSettings(prev => ({ ...prev, [id]: newValue }));

      if (id === 'autoDelete') {
        await updateAziendaSetting(newValue);
      }
    }
  };

  // Provjera uloga (dodao sam toLowerCase() radi sigurnosti)
  const allowedRoles = ['admin', 'superadmin', 'owner'];
  const canManageAutoDelete = allowedRoles.includes(userRole?.toLowerCase());

  const settingsGroups = [
    {
      title: t('settings.group_notifications'),
      items: [
        { id: 'notifications', label: t('settings.push_label'), desc: t('settings.push_desc'), icon: 'Bell' },
        { id: 'emailReports', label: t('settings.email_label'), desc: t('settings.email_desc'), icon: 'Mail' },
      ]
    },
    {
      title: t('settings.group_security'),
      items: [
        { id: 'biometric', label: t('settings.bio_label'), desc: t('settings.bio_desc'), icon: 'Fingerprint' },
        { id: 'autoLogout', label: t('settings.logout_label'), desc: t('settings.logout_desc'), icon: 'LogOut' },
        { id: 'autoDelete', label: t('settings.delete_label'), desc: t('settings.delete_desc'), icon: 'Trash2' },
      ].filter(item => item.id !== 'autoDelete' || canManageAutoDelete)
    },
    {
      title: t('settings.group_preferences'),
      items: [
        { id: 'darkMode', label: t('settings.dark_label'), desc: t('settings.dark_desc'), icon: isDark ? 'Sun' : 'Moon' },
        { id: 'locationService', label: t('settings.gps_label'), desc: t('settings.gps_desc'), icon: 'MapPin' },
      ]
    }
  ];

  const isActive = (id) => id === 'darkMode' ? isDark : settings[id];

  if (loading) return (
    <div className="h-screen flex items-center justify-center bg-background text-primary uppercase font-black animate-pulse tracking-widest">
      Loading Settings...
    </div>
  );

  return (
    <div className="max-w-4xl mx-auto space-y-6 animate-in fade-in slide-in-from-bottom-4 duration-500 pb-10">
      
      {/* Header Kartica */}
      <div className="bg-card border border-border rounded-3xl p-8 shadow-sm">
        <div className="flex items-center gap-6">
          <div className="w-16 h-16 rounded-2xl bg-primary/10 flex items-center justify-center text-primary border border-primary/20 shadow-inner shrink-0">
            <Icon name="Settings" size={32} />
          </div>
          <div>
            <h1 className="text-3xl font-black text-foreground tracking-tight leading-none uppercase">
              {t('settings.title')}
            </h1>
            <p className="text-muted-foreground font-bold mt-1 uppercase text-[10px] tracking-[0.2em] opacity-70">
              {t('settings.subtitle')}
            </p>
          </div>
        </div>
      </div>

      {/* Settings Grupe */}
      <div className="space-y-8">
        {settingsGroups.map((group, gIdx) => (
          <div key={gIdx} className="space-y-4">
            <h2 className="px-2 text-[11px] font-black uppercase text-primary tracking-[0.3em]">
              {group.title}
            </h2>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {group.items.map((item) => (
                <div 
                  key={item.id}
                  onClick={() => toggleSetting(item.id)}
                  className="bg-card border border-border rounded-2xl p-5 hover:border-primary/40 transition-all cursor-pointer group shadow-sm flex items-center justify-between"
                >
                  <div className="flex items-center gap-4">
                    <div className={`p-3 rounded-xl transition-colors ${isActive(item.id) ? 'bg-primary/10 text-primary' : 'bg-muted text-muted-foreground'}`}>
                      <Icon name={item.icon} size={20} />
                    </div>
                    <div>
                      <p className="text-sm font-black text-foreground uppercase tracking-tight leading-none mb-1">
                        {item.label}
                      </p>
                      <p className="text-[10px] font-bold text-muted-foreground uppercase opacity-60 leading-tight">
                        {item.desc}
                      </p>
                    </div>
                  </div>

                  <div className={`w-12 h-6 rounded-full p-1 transition-colors duration-300 ${isActive(item.id) ? 'bg-primary' : 'bg-muted-foreground/30'}`}>
                    <div className={`bg-white w-4 h-4 rounded-full shadow-md transform transition-transform duration-300 ${isActive(item.id) ? 'translate-x-6' : 'translate-x-0'}`} />
                  </div>
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>

      <div className="pt-6 border-t border-border/50 flex flex-col items-center justify-center gap-2">
        <span className="text-[10px] font-black uppercase text-muted-foreground tracking-widest opacity-50">
          {t('settings.version')} 2.4.0-build.88
        </span>
        <button className="text-[10px] font-black uppercase text-destructive hover:underline tracking-widest p-2">
          {t('settings.reset')}
        </button>
      </div>
    </div>
  );
};

export default Settings;